/* iter161 — Ride Mode Background upload modal.
 *
 * Wraps <InteractiveCrop/> in a full-screen dialog sized to the phone
 * screen aspect ratio. On confirm, exports the cropped image as a JPEG
 * blob and stores it in IndexedDB via `lib/rideBgStore.js`. Nothing is
 * uploaded to the server.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Check, Loader2, Upload, LogIn } from "lucide-react";
import InteractiveCrop from "./InteractiveCrop";
import { setUserBackground, clearUserBackground } from "../lib/rideBgStore";
import { useI18n } from "../lib/i18n";
import { getMe } from "../lib/api";

export default function RideBgUploadModal({ isOpen, onClose, hasCurrent, onSaved }) {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [imgSrc, setImgSrc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isGuest, setIsGuest] = useState(true);
  const cropRef = useRef({ getCroppedBlob: null });
  const fileRef = useRef(null);

  // iter176 — Detect guest so the footer can offer "Create Account /
  // Log In" only when it's actionable. Authenticated users don't see
  // it.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getMe().then((me) => { if (!cancelled) setIsGuest(!me); })
      .catch(() => { if (!cancelled) setIsGuest(true); });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImgSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const confirm = async () => {
    if (!cropRef.current?.getCroppedDataURL) return;
    setSaving(true);
    try {
      const dataUrl = cropRef.current.getCroppedDataURL();
      if (!dataUrl) throw new Error("crop failed");
      // Convert data URL → Blob so IndexedDB stores a proper Blob (much
      // smaller than the base64 string and directly renderable via
      // URL.createObjectURL later).
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      await setUserBackground(blob);
      onSaved?.();
      onClose?.();
    } catch {
      /* swallow — modal stays open */
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    await clearUserBackground();
    setSaving(false);
    onSaved?.();
    onClose?.();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="ride-bg-upload-modal"
      style={{ position: "fixed", inset: 0, zIndex: 10001, background: "#0B0B0B", display: "flex", flexDirection: "column" }}
    >
      <header className="flex items-center justify-between px-4 pt-5 pb-3" style={{ color: "#F5F1E8" }}>
        <button data-testid="ride-bg-upload-close" onClick={onClose} className="inline-flex items-center justify-center" style={{ width: 44, height: 44 }}>
          <X className="w-5 h-5" strokeWidth={1.6} />
        </button>
        <h2 data-testid="ride-bg-upload-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
          {/* iter163 — Sheet title reflects whether a background is
              already active. This wording lives ONLY inside the sheet
              — the main Ride Mode screen never surfaces "Change Image". */}
          {hasCurrent
            ? (lang === "es" ? "Cambiar Imagen" : "Change Image")
            : (lang === "es" ? "Fondo Personalizado" : "Custom Background")}
        </h2>
        <div style={{ width: 44 }} />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-5">
        {imgSrc ? (
          (() => {
            // iter176 — Force the crop frame to a strict 9:16 phone
            // aspect so the preview outline matches what will actually
            // render behind the Ride Mode hero (720x1280 canvas). The
            // rounded corners hint at a phone screen shape; anything
            // outside this frame is darkened by the modal backdrop.
            const frameW = Math.min(320, Math.round(window.innerWidth * 0.82));
            const frameH = Math.round(frameW * (16 / 9));
            return (
              <div
                data-testid="ride-bg-crop-frame"
                style={{
                  width: frameW,
                  borderRadius: 24,
                  overflow: "hidden",
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                  border: "1.5px solid rgba(232,184,75,0.4)",
                }}
              >
                <InteractiveCrop
                  src={imgSrc}
                  cropRef={cropRef}
                  frameHeight={frameH}
                  outputSize={{ width: 720, height: 1280 }}
                  minScale={1}
                  maxScale={3}
                  testid="ride-bg-crop"
                />
              </div>
            );
          })()
        ) : (
          <button
            type="button"
            data-testid="ride-bg-upload-pick"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2"
            style={{
              minHeight: 56, padding: "0 22px", borderRadius: 12,
              background: "linear-gradient(135deg, #E7C77E 0%, #E8B84B 55%, #A07820 100%)",
              color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif",
              fontSize: 15, fontWeight: 600,
            }}
          >
            <Upload className="w-4 h-4" strokeWidth={2} />
            {lang === "es" ? "Elegir Imagen" : "Choose Image"}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>

      <footer className="flex flex-col gap-3 p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
        <div className="flex items-center gap-3">
        {hasCurrent && !imgSrc && (
          <button
            type="button"
            data-testid="ride-bg-upload-remove"
            onClick={remove}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2"
            style={{
              minHeight: 48, borderRadius: 10,
              background: "transparent",
              border: "1px solid rgba(230,107,107,0.5)",
              color: "#E66B6B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
            }}
          >
            {lang === "es" ? "Quitar Fondo" : "Remove Background"}
          </button>
        )}
        {imgSrc && (
          <button
            type="button"
            data-testid="ride-bg-upload-save"
            onClick={confirm}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2"
            style={{
              minHeight: 48, borderRadius: 10,
              background: "linear-gradient(135deg, #E7C77E 0%, #E8B84B 55%, #A07820 100%)",
              color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700,
            }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2} />}
            {hasCurrent
              ? (lang === "es" ? "Cambiar Imagen" : "Change Image")
              : (lang === "es" ? "Guardar" : "Save")}
          </button>
        )}
        </div>
        {/* iter176 — Guest-only affordance to log in / create an
         * account. Hidden for authed users to avoid clutter. */}
        {isGuest && (
          <button
            type="button"
            data-testid="ride-bg-upload-login"
            onClick={() => { onClose?.(); navigate("/login"); }}
            className="w-full inline-flex items-center justify-center gap-2"
            style={{
              minHeight: 48, borderRadius: 10,
              background: "transparent",
              border: "1px solid rgba(232,184,75,0.5)",
              color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
            }}
          >
            <LogIn className="w-4 h-4" strokeWidth={1.75} />
            {lang === "es" ? "Crear Cuenta / Iniciar Sesión" : "Create Account / Log In"}
          </button>
        )}
      </footer>
    </div>,
    document.body,
  );
}
